/**
 * Pure helpers for the all-dealers dashboard (WARP-960, WARP-963). Kept free of React so
 * they can be unit-tested on their own.
 *
 * The API sends one row per dealer (`/repair-orders/work-queue/counts/?group_by=dealer`),
 * each with its Work Queue counts and its admin, assistant manager, and manager. This
 * file groups those rows by person.
 *
 * The people are the HubSpot names on each dealer, not WARP users, and they do not form a
 * clean tree: on the 2026-10 prod data 19 admins sit under two assistant managers, and 32
 * people hold more than one role. So a person can appear under two parents. A level's
 * groups split its dealers, so each dealer counts once at every level and the groups
 * always add up to the total.
 */

import { QUEUES } from '@/pages/WorkQueue/queue'

/** The staff fields on a dealer row, as the API names them (camelCased) and filters them. */
export const STAFF = {
  manager: { label: 'Manager', param: 'manager', placeholder: 'Select a Manager' },
  assistantManager: {
    label: 'Asst. manager',
    param: 'assistant_manager',
    placeholder: 'Select an Assistant Manager',
  },
  admin: { label: 'Admin', param: 'admin', placeholder: 'Select an Admin' },
}

/** Each person's level above them, for the "under two assistant managers" note. */
const PARENT = { admin: 'assistantManager', assistantManager: 'manager' }

/**
 * The break-down levels, dealer first and widest last, the order the control shows them
 * in. `path` is the nesting under that level, down to the dealers: Manager opens to its
 * assistant managers, then their admins, then dealers.
 */
export const LEVELS = [
  { id: 'dealer', label: 'Dealer', path: [] },
  { id: 'admin', label: 'Admin', path: ['admin'] },
  { id: 'assistant-manager', label: 'Assistant manager', path: ['assistantManager', 'admin'] },
  { id: 'manager', label: 'Manager', path: ['manager', 'assistantManager', 'admin'] },
]

export const levelById = (id) => LEVELS.find((level) => level.id === id)

/** What a dealer with no one in a role groups under. HubSpot is where to fix it. */
export const UNASSIGNED = 'Not assigned'

const emptyCounts = () => ({
  total: 0,
  late: 0,
  oldestReceivedAt: null,
  queues: Object.fromEntries(QUEUES.map((queue) => [queue.countsKey, {}])),
})

/**
 * Totals across rows, in the shape of one row: every number summed, and the oldest
 * received date the earliest of them. ISO strings in UTC compare in date order.
 */
export const sumRows = (rows) => {
  const sum = emptyCounts()
  rows.forEach((row) => {
    sum.total += row.total || 0
    sum.late += row.late || 0
    if (
      row.oldestReceivedAt &&
      (!sum.oldestReceivedAt || row.oldestReceivedAt < sum.oldestReceivedAt)
    ) {
      sum.oldestReceivedAt = row.oldestReceivedAt
    }
    Object.entries(row.queues || {}).forEach(([key, counts]) => {
      sum.queues[key] = sum.queues[key] || {}
      Object.entries(counts).forEach(([field, value]) => {
        sum.queues[key][field] = (sum.queues[key][field] || 0) + (value || 0)
      })
    })
  })
  return sum
}

const nameOf = (row, field) => row[field] || UNASSIGNED

const trailKey = ({ field, name }) => `${field}:${name}`

/**
 * The levels worth offering for these rows: Dealer always, and a staff level only when
 * more than one person holds it here. An admin looking at their own dealers has nothing
 * to break down by.
 */
export const availableLevels = (rows) =>
  LEVELS.filter(
    (level) =>
      level.id === 'dealer' || new Set(rows.map((row) => nameOf(row, level.path[0]))).size > 1,
  )

/** A filter's names as a list: it takes one name, several, or none. */
const namesIn = (value) => [].concat(value ?? []).filter(Boolean)

/**
 * Rows narrowed by the staff filters and the search box. Each staff filter takes several
 * names, as on the Dealers list: a row matches any of them.
 */
export const filterRows = (rows, { query, ...staff } = {}) => {
  const needle = (query || '').trim().toLowerCase()
  const chosen = Object.keys(STAFF)
    .map((field) => [field, namesIn(staff[field])])
    .filter(([, names]) => names.length > 0)
  return rows.filter(
    (row) =>
      chosen.every(([field, names]) => names.includes(nameOf(row, field))) &&
      (!needle || row.name.toLowerCase().includes(needle)),
  )
}

/** The names a staff filter can offer, given the other filters already chosen. */
export const staffOptions = (rows, field, filters) =>
  [
    ...new Set(
      filterRows(rows, { ...filters, [field]: undefined, query: '' }).map((row) =>
        nameOf(row, field),
      ),
    ),
  ].sort((a, b) => (a === UNASSIGNED) - (b === UNASSIGNED) || a.localeCompare(b))

/**
 * The tree for a level. Group nodes carry `field`, `name`, the `trail` of names above
 * them, `parents` (how many people one level up this person's dealers report to, across
 * every row in scope, not just the filtered ones), and `isSelf` when they are also the
 * person directly above. Dealer nodes carry the `row`.
 */
export const buildTree = (rows, levelId, scopeRows = rows) => {
  const { path } = levelById(levelId)

  const build = (subset, depth, trail) => {
    if (depth === path.length) {
      return subset.map((row) => ({
        type: 'dealer',
        key: [...trail.map(trailKey), row.id].join('›'),
        name: row.name,
        row,
        rows: [row],
        counts: sumRows([row]),
        depth,
      }))
    }

    const field = path[depth]
    const groups = new Map()
    subset.forEach((row) => {
      const name = nameOf(row, field)
      if (!groups.has(name)) groups.set(name, [])
      groups.get(name).push(row)
    })

    return [...groups.entries()].map(([name, groupRows]) => {
      const parentField = PARENT[field]
      const parents =
        parentField && name !== UNASSIGNED
          ? new Set(
              scopeRows
                .filter((row) => nameOf(row, field) === name)
                .map((row) => nameOf(row, parentField)),
            ).size
          : 1
      return {
        type: 'group',
        key: [...trail, { field, name }].map(trailKey).join('›'),
        field,
        name,
        trail,
        rows: groupRows,
        counts: sumRows(groupRows),
        depth,
        parents,
        isSelf: depth > 0 && trail[trail.length - 1]?.name === name,
        children: build(groupRows, depth + 1, [...trail, { field, name }]),
      }
    })
  }

  return build(rows, 0, [])
}

/** A sortable column's value for a node. */
const sortValue = (node, key) => {
  if (key === 'name') return node.name.toLowerCase()
  if (key === 'dealers') return node.rows.length
  if (key === 'admin') return (node.row?.admin || '').toLowerCase()
  if (key === 'oldest') return node.counts.oldestReceivedAt
  return node.counts.queues[key]?.total ?? 0
}

/**
 * Sorts a tree in place, every level. Oldest sorts the oldest first when descending, and
 * puts nodes with nothing waiting last either way. Ties go by name.
 */
export const sortTree = (nodes, { key, direction }) => {
  const sign = direction === 'asc' ? 1 : -1
  nodes.sort((a, b) => {
    const va = sortValue(a, key)
    const vb = sortValue(b, key)
    if (key === 'oldest') {
      if (va !== vb) {
        if (!va) return 1
        if (!vb) return -1
        // An earlier date is older, so "most first" (desc) means ascending dates.
        return (va < vb ? -1 : 1) * -sign
      }
    } else if (va !== vb) {
      return (va < vb ? -1 : 1) * sign
    }
    return a.name.localeCompare(b.name)
  })
  nodes.forEach((node) => node.children && sortTree(node.children, { key, direction }))
  return nodes
}

/** The dealer holding the oldest Ready or Closed RO: where to start (Michelle). */
export const startingDealer = (rows) =>
  rows.reduce(
    (oldest, row) =>
      row.oldestReceivedAt && (!oldest || row.oldestReceivedAt < oldest.oldestReceivedAt)
        ? row
        : oldest,
    null,
  )

/**
 * Where a count opens: that Work Queue tab, narrowed the way the row is. A dealer's own
 * queue for a dealer row; for a person, the all-dealers queue with their name and the
 * names above them, plus the page's staff filters. `filter` opens the tab with one of its
 * summary cards on (`late`, `waiting`, …). Null for a group with no one named, since the
 * API cannot filter on a missing name.
 */
export const queueLink = (queue, { dealerId = null, staff = [], filter = null } = {}) => {
  if (staff.some(({ name }) => name === UNASSIGNED)) return null
  const params = new URLSearchParams()
  if (queue.id !== QUEUES[0].id) params.set('tab', queue.id)
  if (filter) params.set('filter', filter)
  staff.forEach(({ field, name }) => params.append(STAFF[field].param, name))
  const query = params.toString()
  return `/work-queue${dealerId ? `/${dealerId}` : ''}${query ? `?${query}` : ''}`
}

/** The staff a node narrows by: its trail and itself, plus the page's filters. */
export const staffForNode = (node, filters = {}) => {
  const fromFilters = Object.keys(STAFF).flatMap((field) =>
    namesIn(filters[field]).map((name) => ({ field, name })),
  )
  if (node.type !== 'group') return fromFilters
  const own = [...node.trail, { field: node.field, name: node.name }]
  return [...fromFilters.filter((f) => !own.some((o) => o.field === f.field)), ...own]
}
